import { useId, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { CountryCodePicker } from "@/components/crm/CountryCodePicker";
import { CrmSelect, crmSelectFieldWrapperClass } from "@/components/crm/CrmListChevron";
import {
  coerceEmailRowType,
  coerceSocialPlatform,
  emptyEmailRow,
  emptyPhoneRow,
  emptySocialRow,
  displayCrmEmailTypeLabel,
  isValidCrmEmailAddress,
  formatCrmSocialPlatformLabel,
  mergeEmailTypesForEditor,
  mergeSocialPlatformsForEditor,
  type CrmChannelTypeOptions,
  type CrmContactChannelsState,
  type CrmContactEmail,
  type CrmContactEmailContext,
  type CrmContactPhone,
  type CrmContactSocial,
  type CrmContactSocialPlatform,
} from "@/lib/crmContactChannels";
import { CrmSocialPlatformIcon } from "@/components/crm/CrmSocialPlatformIcon";
import { CrmContactAddressesEditor } from "@/components/crm/CrmContactAddressesEditor";
import { coerceCountryCodeId } from "@/lib/countryCodes";
import type { CrmAddressTypeOptions } from "@/lib/crmContactAddresses";

const fieldClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20";
const selectTextClass = "bg-transparent py-2 pl-3 text-sm";

function fallbackTypeOptions(
  emailOptions: readonly CrmContactEmail["type"][],
  socialOptions: readonly CrmContactSocialPlatform[],
  emailLabels: CrmChannelTypeOptions["emailLabels"] = {},
  socialIconUrls: CrmChannelTypeOptions["socialIconUrls"] = {},
): CrmChannelTypeOptions {
  return {
    emailTypes: [...emailOptions],
    emailLabels,
    socialPlatforms: [...socialOptions],
    socialIconUrls,
  };
}

type CrmContactChannelsEditorProps = {
  value: CrmContactChannelsState;
  onChange: (next: CrmContactChannelsState) => void;
  disabled?: boolean;
  emailContext?: CrmContactEmailContext;
  typeOptions?: CrmChannelTypeOptions;
  /** Default dial-code country for new phone rows (from Lead field settings). */
  defaultPhoneCountryId?: string;
  addressTypeOptions?: CrmAddressTypeOptions;
  /** Default ISO country for new address rows (from Lead field settings). */
  defaultAddressCountryId?: string;
  /** When false, hide the address section (Lead field settings). */
  showAddresses?: boolean;
};

export function CrmContactChannelsEditor({
  value,
  onChange,
  disabled,
  emailContext = "contact",
  typeOptions,
  defaultPhoneCountryId = "us",
  addressTypeOptions,
  defaultAddressCountryId = "us",
  showAddresses = true,
}: CrmContactChannelsEditorProps) {
  const primaryEmailGroup = useId();
  const [emailTouched, setEmailTouched] = useState<Record<number, boolean>>({});
  const baseEmailOptions = typeOptions?.emailTypes.length
    ? typeOptions.emailTypes
    : emailContext === "account"
      ? (["company", "business", "personal"] as const)
      : (["business", "personal"] as const);
  const baseSocialOptions = typeOptions?.socialPlatforms.length
    ? typeOptions.socialPlatforms
    : ([
        "linkedin",
        "twitter",
        "facebook",
        "instagram",
        "youtube",
        "github",
        "website",
      ] as const);
  const socialIconUrls = typeOptions?.socialIconUrls ?? {};

  const emails = value.emails.length ? value.emails : [emptyEmailRow(emailContext)];
  const phones = value.phones;
  const socials = value.socials;
  const addresses = value.addresses;

  const emailOptions = mergeEmailTypesForEditor([...baseEmailOptions], emails);
  const socialOptions = mergeSocialPlatformsForEditor([...baseSocialOptions], socials);

  const emailLabel = (type: CrmContactEmail["type"]) =>
    displayCrmEmailTypeLabel(
      typeOptions?.emailLabels[type],
      type,
      emailContext,
    );

  const emailRowError = (index: number, address: string): string | null => {
    if (!emailTouched[index]) return null;
    const trimmed = address.trim();
    if (!trimmed) return null;
    return isValidCrmEmailAddress(trimmed) ? null : "Enter a valid email address.";
  };

  const setEmails = (next: CrmContactEmail[]) => onChange({ ...value, emails: next });
  const setPhones = (next: CrmContactPhone[]) => onChange({ ...value, phones: next });
  const setSocials = (next: CrmContactSocial[]) => onChange({ ...value, socials: next });
  const setAddresses = (next: import("@/lib/crmContactAddresses").CrmContactAddress[]) =>
    onChange({ ...value, addresses: next });

  const resolvedDefaultCountryId = coerceCountryCodeId(defaultPhoneCountryId);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Email</h3>
          <button
            type="button"
            disabled={disabled}
            onClick={() =>
              setEmails([
                ...emails,
                { ...emptyEmailRow(emailContext), isPrimary: false, type: emailOptions[0] || "business" },
              ])
            }
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add email
          </button>
        </div>
        {emails.map((row, index) => {
          const rowError = emailRowError(index, row.address);
          return (
          <div key={`email-${index}`} className="flex flex-wrap items-center gap-2">
            <div
              className={`flex min-w-[min(100%,14rem)] flex-1 items-stretch overflow-visible rounded-md border ${
                rowError
                  ? "border-red-500 dark:border-red-400"
                  : "border-gray-300 dark:border-white/20"
              }`}
            >
              <CrmSelect
                layout="inline"
                wrapperClassName="self-stretch border-r border-gray-300 dark:border-white/20 dark:bg-black/20"
                disabled={disabled}
                value={coerceEmailRowType(
                  row.type,
                  emailContext,
                  typeOptions ?? fallbackTypeOptions(emailOptions, socialOptions),
                )}
                onChange={(e) => {
                  const next = [...emails];
                  next[index] = {
                    ...row,
                    type: coerceEmailRowType(
                      e.target.value as CrmContactEmail["type"],
                      emailContext,
                      typeOptions ??
                        fallbackTypeOptions(emailOptions, socialOptions, {}, socialIconUrls),
                    ),
                  };
                  setEmails(next);
                }}
                className="h-full bg-transparent py-2 pl-2 text-sm"
                aria-label="Email type"
              >
                {emailOptions.map((t) => (
                  <option key={t} value={t}>
                    {emailLabel(t)}
                  </option>
                ))}
              </CrmSelect>
              <input
                disabled={disabled}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={row.address}
                onChange={(e) => {
                  const next = [...emails];
                  next[index] = { ...row, address: e.target.value };
                  setEmails(next);
                }}
                onBlur={() => setEmailTouched((prev) => ({ ...prev, [index]: true }))}
                placeholder={
                  emailContext === "account" ? "info@companydomain.com" : "Email address"
                }
                className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-slate-400 dark:bg-black/20"
                aria-label="Email address"
                aria-invalid={rowError ? true : undefined}
              />
            </div>
            <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <input
                type="radio"
                name={primaryEmailGroup}
                disabled={disabled}
                checked={Boolean(row.isPrimary)}
                onChange={() =>
                  setEmails(emails.map((e, i) => ({ ...e, isPrimary: i === index })))
                }
              />
              Primary
            </label>
            <button
              type="button"
              disabled={disabled || emails.length <= 1}
              aria-label="Remove email"
              onClick={() => {
                const next = emails.filter((_, i) => i !== index);
                setEmails(next.length ? next : [emptyEmailRow(emailContext)]);
                setEmailTouched((prev) => {
                  const rebuilt: Record<number, boolean> = {};
                  for (const [key, val] of Object.entries(prev)) {
                    const i = Number(key);
                    if (i < index) rebuilt[i] = val;
                    else if (i > index) rebuilt[i - 1] = val;
                  }
                  return rebuilt;
                });
              }}
              className="rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:border-white/20 dark:text-gray-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            {rowError ? (
              <p className="w-full text-xs text-red-600 dark:text-red-400" role="alert">
                {rowError}
              </p>
            ) : null}
          </div>
        );
        })}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Phone</h3>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setPhones([...phones, emptyPhoneRow(resolvedDefaultCountryId)])}
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add phone
          </button>
        </div>
        {phones.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">No phone numbers yet.</p>
        ) : (
          phones.map((row, index) => {
            const countryId = coerceCountryCodeId(row.countryId, resolvedDefaultCountryId);
            return (
              <div key={`phone-${index}`} className="flex flex-wrap items-center gap-2">
                <div
                  className={`flex min-w-[min(100%,14rem)] flex-1 items-stretch overflow-visible rounded-md border border-gray-300 dark:border-white/20`}
                >
                  <CountryCodePicker
                    value={countryId}
                    disabled={disabled}
                    variant="inline"
                    onChange={(nextId) => {
                      const next = [...phones];
                      next[index] = { ...row, countryId: nextId };
                      setPhones(next);
                    }}
                  />
                  <input
                    disabled={disabled}
                    value={row.number}
                    onChange={(e) => {
                      const next = [...phones];
                      next[index] = { ...row, number: e.target.value };
                      setPhones(next);
                    }}
                    placeholder="Phone number"
                    className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-slate-400 dark:bg-black/20"
                    aria-label="Phone number"
                  />
                </div>
                <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={Boolean(row.smsOptIn)}
                    onChange={(e) => {
                      const next = [...phones];
                      next[index] = { ...row, smsOptIn: e.target.checked };
                      setPhones(next);
                    }}
                  />
                  SMS
                </label>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label="Remove phone"
                  onClick={() => setPhones(phones.filter((_, i) => i !== index))}
                  className="rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {showAddresses ? (
        <CrmContactAddressesEditor
          addresses={addresses}
          onChange={setAddresses}
          disabled={disabled}
          addressTypeOptions={addressTypeOptions}
          defaultCountryId={defaultAddressCountryId}
        />
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Social</h3>
          <button
            type="button"
            disabled={disabled}
            onClick={() =>
              setSocials([
                ...socials,
                {
                  ...emptySocialRow(),
                  isPrimary: false,
                  platform: socialOptions[0] || "linkedin",
                },
              ])
            }
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add social
          </button>
        </div>
        {socials.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">No social profiles yet.</p>
        ) : (
          socials.map((row, index) => {
            const platform = coerceSocialPlatform(
              row.platform,
              typeOptions ?? fallbackTypeOptions(emailOptions, socialOptions, {}, socialIconUrls),
            );
            return (
              <div key={`social-${index}`} className="flex flex-wrap items-center gap-2">
                <CrmSelect
                  wrapperClassName={`w-36 shrink-0 ${crmSelectFieldWrapperClass}`}
                  disabled={disabled}
                  value={platform}
                  onChange={(e) => {
                    const next = [...socials];
                    next[index] = {
                      ...row,
                      platform: coerceSocialPlatform(
                        e.target.value as CrmContactSocialPlatform,
                        typeOptions ??
                          fallbackTypeOptions(emailOptions, socialOptions, {}, socialIconUrls),
                      ),
                    };
                    setSocials(next);
                  }}
                  className={selectTextClass}
                  aria-label="Social platform"
                >
                  {socialOptions.map((p) => (
                    <option key={p} value={p}>
                      {formatCrmSocialPlatformLabel(p)}
                    </option>
                  ))}
                </CrmSelect>
                <input
                  disabled={disabled}
                  value={row.url}
                  onChange={(e) => {
                    const next = [...socials];
                    next[index] = { ...row, url: e.target.value };
                    setSocials(next);
                  }}
                  placeholder="URL or handle"
                  className={`${fieldClass} min-w-[12rem] flex-1`}
                />
                <CrmSocialPlatformIcon
                  platform={platform}
                  iconUrl={socialIconUrls[platform]}
                />
                <button
                  type="button"
                  disabled={disabled}
                  aria-label="Remove social"
                  onClick={() => setSocials(socials.filter((_, i) => i !== index))}
                  className="rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
